import { useEffect, useState } from 'react';
import { BadgeCheck, FileWarning } from 'lucide-react';
import { chamarApi, ErroApi } from '../../nucleo/auth/cliente-api';
import { PageHeader } from '../../ui/PageHeader';
import { Alert, AlertDescription } from '../../ui/base/alert';
import { Button } from '../../ui/base/button';
import { Input } from '../../ui/base/input';
import { Label } from '../../ui/base/label';
const NS = '/bff/v1/j12-s03-verificar-documento';
type S = {
  titulo: string;
  descricao: string;
  resultado: null | {
    situacao: string;
    codigo?: string;
    formato?: string;
    sha256?: string;
    tamanhoBytes?: number;
    instanteCorte?: string;
    emitidoEm?: string;
    revogadoEm?: string;
  };
  alertas: Array<{ mensagem: string }>;
  privacidade: string;
};
type E = { state: S };
export function VerificarDocumentoPage() {
  const [e, se] = useState<S | null>(null),
    [codigo, setCodigo] = useState(''),
    [arquivo, setArquivo] = useState<string | undefined>(),
    [erro, setErro] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void chamarApi<E>(`${NS}/state`, { publica: true }).then((x) => se(x.state));
  }, []);
  async function verificar() {
    setBusy(true);
    setErro(null);
    try {
      se(
        (
          await chamarApi<E>(`${NS}/verbs/${arquivo ? 'verificar_arquivo' : 'verificar_codigo'}`, {
            metodo: 'POST',
            corpo: { codigo: codigo.toUpperCase(), arquivoBase64: arquivo },
            publica: true,
          })
        ).state,
      );
    } catch (x) {
      setErro(x instanceof ErroApi ? x.aviso : 'Não foi possível verificar.');
    } finally {
      setBusy(false);
    }
  }
  async function ler(f: File) {
    setArquivo(btoa(String.fromCharCode(...new Uint8Array(await f.arrayBuffer()))));
  }
  return (
    <section className="mx-auto w-full max-w-2xl px-4 py-8">
      <PageHeader
        title={e?.titulo ?? 'Verificar documento oficial'}
        description={e?.descricao ?? 'Consulte a autenticidade sem expor conteúdo restrito.'}
      />
      {erro && (
        <Alert>
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-3 rounded-md border p-4">
        <Label htmlFor="codigo">Código de verificação</Label>
        <Input
          id="codigo"
          maxLength={16}
          value={codigo}
          onChange={(x) => setCodigo(x.target.value.replace(/[^0-9A-Fa-f]/g, ''))}
        />
        <Label htmlFor="arquivo">Arquivo para conferir os bytes (opcional)</Label>
        <Input
          id="arquivo"
          type="file"
          accept="application/json,text/csv"
          onChange={(x) => {
            const f = x.target.files?.[0];
            if (f) void ler(f);
            else setArquivo(undefined);
          }}
        />
        <Button disabled={busy || codigo.length !== 16} onClick={() => void verificar()}>
          Verificar autenticidade
        </Button>
      </div>
      {e?.resultado && (
        <article className="mt-4 rounded-md border p-4">
          {e.resultado.situacao === 'AUTENTICO' ? (
            <BadgeCheck className="h-8 w-8 text-primary" />
          ) : (
            <FileWarning className="h-8 w-8 text-destructive" />
          )}
          <h2 className="mt-2 text-lg font-semibold">{e.resultado.situacao}</h2>
          {e.resultado.codigo && (
            <p className="mt-2 break-all text-sm">
              Código {e.resultado.codigo} · {e.resultado.formato} · {e.resultado.tamanhoBytes} bytes
              <br />
              SHA-256 {e.resultado.sha256}
            </p>
          )}
        </article>
      )}
      <p className="mt-4 text-sm text-muted-foreground">{e?.privacidade}</p>
    </section>
  );
}
